import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearAppointmentAuditData,
    clearNewAppointment,
    loadAppointmentAuditData,
    setOpenAppointmentSchedulerPanel,
    setUsersCurrentAppointment
} from '../../../store/calendarService';
import { loadServiceDetailsByService } from '../../../store/directusService';
import { setCurrentlySelectedParticipant } from '../../../store/participantService';
import { reverseFormatDate } from '../../../utils/dateFunctions';
import { hasRole } from '../../../utils/userRoles';
import AuditAppointmentModal from '../../auditing/auditAppointments/AuditAppointmentModal';
import StaticField from '../../formElements/StaticField';
import Switch from '../../formElements/Switch';
import Panel from '../../ui/panel/Panel';
import MandatoryActivitiesSwitch from '../components/MandatoryActivitiesSwitch';
import StartAppointmentRow from '../components/StartAppointmentRow';
import StaticParticipant from '../components/StaticParticipant';
import { auditRoles, participantAccessRoles } from '../utils/calendarUtils';

import sidePanel from '../styles/schedulePanel.module.css';

const UserAppointmentView = ({
    usersCurrentAppointment,
    currentCalendarOwner,
    currentLocation,
    currentParticipantEntry
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = { id: '', service: '' };
    const [newEntry, setNewEntry] = useState(initialState);
    const [isAuditable, setIsAuditable] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [modalHeadingLabel, setModalHeadingLabel] = useState('');
    const [isAuditDisabled, setIsAuditDisabled] = useState(false);
    const [hasParticipantAccess, setHasParticipantAccess] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const serviceDetailsById = useSelector(
        (state) => state.entities.directusService.serviceDetailsById
    );
    const openAppointmentSchedulerPanel = useSelector(
        (state) => state.entities.calendarService.openAppointmentSchedulerPanel
    );

    // USE EFFECTS
    useEffect(() => {
        if (!currentCalendarOwner || Object.keys(currentCalendarOwner).length < 1) return;
        const { serviceId } = usersCurrentAppointment;
        if (!(serviceId in serviceDetailsById)) dispatch(loadServiceDetailsByService([serviceId]));
        if (currentParticipantEntry && 'firstName' in currentParticipantEntry) {
            dispatch(setCurrentlySelectedParticipant(currentParticipantEntry));
        }
    }, []);

    useEffect(() => {
        if (!roles?.length) return;
        setIsAuditDisabled(!hasRole(auditRoles, roles));
        setHasParticipantAccess(hasRole(participantAccessRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (newEntry.id || !(usersCurrentAppointment?.serviceId in serviceDetailsById)) return;
        setNewEntry({
            id: usersCurrentAppointment?.id,
            service: serviceDetailsById[usersCurrentAppointment.serviceId]?.name
        });
    }, [serviceDetailsById]);

    // EVENT HANDLERS
    const onPanelClose = () => {
        setNewEntry(initialState);
        dispatch(setUsersCurrentAppointment({}));
        dispatch(setOpenAppointmentSchedulerPanel(false));
        dispatch(clearNewAppointment());
    };

    const onAuditModalClose = (e, reason) => {
        e.stopPropagation();
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        setModalHeadingLabel('');
        dispatch(clearAppointmentAuditData());
    };

    const onSearchAuditItems = (label, modalHeadingLabel) => {
        if (newEntry.id) dispatch(loadAppointmentAuditData('appointment', newEntry.id, label));
        setShowAuditModal(true);
        setAuditLabel(label);
        setModalHeadingLabel(modalHeadingLabel ?? label);
    };

    // RENDER
    return (
        <>
            <Panel width="500px" open={openAppointmentSchedulerPanel} onToggle={onPanelClose}>
                <div className={sidePanel.panelActions}>
                    <Switch
                        id={'auditDisplayFields'}
                        label="Show Audit Fields"
                        disabled={isAuditDisabled}
                        onOff={true}
                        customClass="auditSwitch"
                        checked={isAuditable}
                        onChange={(e) => setIsAuditable(e.target.checked)}
                    />
                </div>

                <div className={sidePanel.formWrapper}>
                    <StaticField content={newEntry.service} customClass="underlinedContent" />
                    <StaticField label="Adviser" content={currentCalendarOwner.loginEmailAddress} />
                    <StaticField label="Appointment Type" content={usersCurrentAppointment.type} />
                    <StaticField
                        label="Location"
                        content={currentLocation}
                        isAuditable={isAuditable}
                        onSearchAuditItems={onSearchAuditItems}
                    />
                    {usersCurrentAppointment?.remoteCallUrl && (
                        <StaticField
                            id="remoteCallUrl"
                            label="Remote Call URL"
                            content={usersCurrentAppointment.remoteCallUrl}
                        />
                    )}
                    {usersCurrentAppointment?.additionalDetails && (
                        <StaticField
                            id="additionalDetails"
                            label="Additional Details"
                            content={usersCurrentAppointment.additionalDetails}
                        />
                    )}
                    <StaticField
                        label="Start Time"
                        content={usersCurrentAppointment.startTime}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Start Time')}
                    />
                    <StaticField
                        label="End Time"
                        content={usersCurrentAppointment.endTime}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('End Date', 'End Time')}
                    />
                    <StaticField
                        label="Date"
                        content={reverseFormatDate(usersCurrentAppointment.date)}
                        isAuditable={isAuditable}
                        onSearchAuditItems={() => onSearchAuditItems('Start Date', 'Date')}
                    />
                    <StaticParticipant
                        participant={currentParticipantEntry}
                        hasParticipantAccess={hasParticipantAccess}
                        onNav={onPanelClose}
                    />
                    <MandatoryActivitiesSwitch
                        isMandatory={usersCurrentAppointment.mandatory}
                        disabled={true}
                    />

                    {currentParticipantEntry?.firstName && (
                        <StartAppointmentRow
                            disabled={true}
                            isUpdateDisabled={true}
                            hasParticipantAccess={hasParticipantAccess}
                            onPanelClose={onPanelClose}
                            appointment={usersCurrentAppointment}
                            participant={currentParticipantEntry}
                            isAuditable={isAuditable}
                            onSearchAuditItems={onSearchAuditItems}
                        />
                    )}
                </div>
            </Panel>
            {showAuditModal && (
                <div id="audit">
                    <AuditAppointmentModal
                        isOpen={showAuditModal}
                        label={auditLabel}
                        modalHeadingLabel={modalHeadingLabel}
                        onClose={onAuditModalClose}
                    />
                </div>
            )}
        </>
    );
};

UserAppointmentView.propTypes = {
    usersCurrentAppointment: PropTypes.object,
    currentCalendarOwner: PropTypes.object,
    currentLocation: PropTypes.string,
    currentParticipantEntry: PropTypes.object
};

export default UserAppointmentView;
